import React from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string, country: any) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  country?: string;
  preferredCountries?: string[];
  enableAreaCodes?: boolean;
  enableSearch?: boolean;
  excludeCountries?: string[];
  onlyCountries?: string[];
  autoFormat?: boolean;
  name?: string;
  disableCountrySelect?: boolean;
  variant?: string;
  size?: string;
}

const CustomPhoneInput: React.FC<PhoneInputProps> = ({
  value = '',
  onChange,
  onBlur,
  error = false,
  helperText,
  label,
  placeholder = 'Enter phone number',
  disabled = false,
  required = false,
  country = 'us',
  preferredCountries,
  enableAreaCodes = false,
  enableSearch = true,
  excludeCountries = [],
  onlyCountries = [],
  autoFormat = true,
  name,
  disableCountrySelect = false,
  size = 'medium',
}) => {
  return (
    <div style={{ width: '100%' }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: 12,
            fontWeight: 500,
            marginBottom: 4,
            color: error ? 'var(--err)' : 'var(--ink-2)',
          }}
        >
          {label}
          {required && <span style={{ color: 'var(--err)', marginLeft: 4 }}>*</span>}
        </label>
      )}

      {/* Style overrides scoped to this wrapper */}
      <style>{`
        .phone-input-ds .react-tel-input { width: 100%; }
        .phone-input-ds .react-tel-input .form-control {
          width: 100%;
          height: ${size === 'small' ? '36px' : '42px'};
          font-size: ${size === 'small' ? '13px' : '15px'};
          border: 1px solid ${error ? 'var(--err)' : 'var(--line)'};
          border-radius: 8px;
          padding-left: 60px;
          padding-right: 14px;
          background: var(--bg-elev);
          color: var(--ink);
          font-family: inherit;
          transition: border-color 0.15s;
          outline: none;
        }
        .phone-input-ds .react-tel-input .form-control:hover {
          border-color: var(--ink-3);
        }
        .phone-input-ds .react-tel-input .form-control:focus {
          border-color: var(--brand-blue);
          box-shadow: 0 0 0 2px rgba(59,130,246,0.15);
        }
        .phone-input-ds .react-tel-input .flag-dropdown {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: 52px;
          border: none;
          background: transparent;
          border-radius: 8px 0 0 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .phone-input-ds .react-tel-input .flag-dropdown:hover {
          background: var(--bg-hover);
        }
        .phone-input-ds .react-tel-input .selected-flag {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border: none;
          background: none;
        }
        .phone-input-ds .react-tel-input .country-list {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 4px;
          max-height: 200px;
          overflow-y: auto;
          border-radius: 8px;
          box-shadow: var(--shd-2);
          border: 1px solid var(--line);
          background: var(--bg-elev);
          z-index: 9999;
        }
        .phone-input-ds .react-tel-input .country-list .country {
          padding: 10px 14px;
          color: var(--ink);
          font-size: 13px;
          cursor: pointer;
          display: flex;
          align-items: center;
        }
        .phone-input-ds .react-tel-input .country-list .country:hover {
          background: var(--bg-hover);
        }
        .phone-input-ds .react-tel-input .country-list .country.highlight {
          background: var(--brand-blue);
          color: #fff;
        }
        .phone-input-ds .react-tel-input .country-list .country .country-name {
          margin-left: 8px;
        }
        .phone-input-ds .react-tel-input .country-list .country .dial-code {
          color: var(--ink-3);
          font-size: 12px;
          margin-left: 6px;
        }
        .phone-input-ds .react-tel-input .country-list .country.highlight .dial-code {
          color: rgba(255,255,255,0.8);
        }
        .phone-input-ds .react-tel-input .country-list .search {
          position: sticky;
          top: 0;
          background: var(--bg-elev);
          border-bottom: 1px solid var(--line);
          padding: 8px;
        }
        .phone-input-ds .react-tel-input .country-list .search input {
          width: 100%;
          padding: 6px 10px;
          border: 1px solid var(--line);
          border-radius: 6px;
          font-size: 13px;
          background: var(--bg);
          color: var(--ink);
          outline: none;
        }
      `}</style>

      <div className="phone-input-ds">
        <PhoneInput
          country={country}
          value={value}
          onChange={(val, ctry) => onChange?.(val, ctry)}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          preferredCountries={preferredCountries || ['us', 'gb', 'ca', 'au', 'il']}
          enableAreaCodes={enableAreaCodes}
          enableAreaCodeStretch={false}
          enableLongNumbers={false}
          enableSearch={enableSearch}
          excludeCountries={excludeCountries}
          onlyCountries={onlyCountries.length > 0 ? onlyCountries : undefined}
          autoFormat={autoFormat}
          searchPlaceholder="Search countries..."
          searchStyle={{ margin: '0', width: '100%', height: '36px' }}
          inputProps={{
            name,
            'aria-invalid': error,
            'aria-describedby': error && helperText ? `${name}-helper-text` : undefined,
            autoComplete: 'tel',
          }}
          containerClass="react-tel-input"
          inputClass={`form-control ${size === 'small' ? 'form-control-small' : ''}`}
          buttonClass="flag-dropdown"
          dropdownClass="country-list"
          searchClass="search"
          disableCountryCode={false}
          disableDropdown={disableCountrySelect || disabled}
          countryCodeEditable={false}
          specialLabel=""
        />
      </div>

      {helperText && (
        <p
          id={error ? `${name}-helper-text` : undefined}
          style={{
            margin: '4px 0 0',
            fontSize: 11,
            color: error ? 'var(--err)' : 'var(--ink-3)',
          }}
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

CustomPhoneInput.displayName = 'PhoneInput';

export default CustomPhoneInput;
